import { useState } from 'react'

import type { DateRange } from 'react-day-picker'

import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'
import { useCreateHighSpenderListMutation } from '@/modules/tracking/new-subscriber-analysis/hooks/use-create-high-spender-list.ts'
import { useCreateLowSpenderListMutation } from '@/modules/tracking/new-subscriber-analysis/hooks/use-create-low-spender-list.ts'
import { useSpendingList } from '@/modules/tracking/new-subscriber-analysis/hooks/use-spending-list.ts'
import { CreateSpendingCustomerList } from '@/modules/tracking/new-subscriber-analysis/ui/dialog/create-spending-customer-list.tsx'
import { CustomerListTable } from '@/modules/tracking/new-subscriber-analysis/ui/table/customer-list-table.tsx'
import { usePagination } from '@/utils/pagination/pagination-context.tsx'

interface Props {
  title: string
  description: string
  isOpen: boolean
  onClose: () => void
  date: DateRange
  creatorId: number
  spendLimit: number
  high?: boolean
}
interface CreateFanListModalState {
  placeholder: string
  isOpen: boolean
}

const HighSpendingTexts = {
  title: 'High-Spending Customer List',
  description:
    'In this List you can see all the new High-Spending Customers within your set timeframe and minimum spend. You can create a list or view the High-Spending Customers individually.',
}

const LowSpendingTexts = {
  title: 'Low-Spending Customer List',
  description:
    'In this List you can see all the new Low-Spending Customers within your set timeframe and maximum spend. You can create a list or view the Low-Spending Customers individually.',
}

export const SpendingCustomerListDialog = ({
  isOpen,
  onClose,
  date,
  creatorId,
  high,
  title,
  description,
  spendLimit,
}: Props) => {
  const [createFanListModal, setCreateFanListModal] =
    useState<CreateFanListModalState>({
      placeholder: '',
      isOpen: false,
    })
  const { pagination } = usePagination()

  const { spendingList, isFetching } = useSpendingList(
    date,
    creatorId,
    pagination,
    !!high,
    spendLimit,
    isOpen,
  )

  const createLowSpenderListMutation = useCreateLowSpenderListMutation(
    date,
    spendLimit,
  )

  const createHighSpenderListMutation = useCreateHighSpenderListMutation(
    date,
    spendLimit,
  )

  const texts = high ? HighSpendingTexts : LowSpendingTexts

  const handleCloseCreateFanListModal = () => {
    setCreateFanListModal((prev) => ({ ...prev, isOpen: false }))
  }

  const handleOpenCreateFansListClick = () => {
    setCreateFanListModal((prev) => ({ ...prev, isOpen: true }))
  }

  const handleCreateFansList = (name: string) => {
    const onSuccess = () => {
      handleCloseCreateFanListModal()
    }

    if (high) {
      createHighSpenderListMutation.mutate(name, {
        onSuccess,
      })

      return
    }

    createLowSpenderListMutation.mutate(name, {
      onSuccess,
    })
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      showCloseIcon={false}
      panelClassName="lg:max-w-3xl"
      childrenClassName="max-w-full overflow-hidden"
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>{description}</Dialog.Description>
        </Dialog.Header>
        <div className="relative max-h-[548px]">
          <CustomerListTable
            spendingList={spendingList || null}
            isFetching={isFetching}
          />
        </div>
        <Dialog.Footer>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            className="w-full"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            className="flex w-full items-center gap-2"
            isLoading={
              createHighSpenderListMutation.isLoading ||
              createLowSpenderListMutation.isLoading
            }
            onClick={handleOpenCreateFansListClick}
            disabled={!spendingList?.content.length}
          >
            Create List
          </Button>
        </Dialog.Footer>
      </div>
      <CreateSpendingCustomerList
        onClose={handleCloseCreateFanListModal}
        onCreate={handleCreateFansList}
        title={texts.title}
        description={texts.description}
        {...createFanListModal}
      />
    </Dialog>
  )
}
